import { React, useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageTitle from "../../PageTitle";
import useAxios from "../../../utilities/useAxios";
import {
	Calendar,
	Card,
	Space,
	Tag,
	message,
	List,
	Button,
	Modal,
	Input,
	Layout,
	Tabs,
	Checkbox,
} from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { ConfigProvider } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { useNavigate } from "react-router-dom";
import RentSanctuaryNavigation from "./navigation/RentSanctuaryNavigation";
import locale from "antd/lib/locale/ko_KR";
import { fetchKoreanHolidays } from "../../../utilities/koreanHolidays";
import { createCalendarHolidayRenders } from "../calendar/calendarHolidayCellRender";
import {
	formatSanctuaryRoomTag,
	sanctuaryScheduleTagColor,
	truncateCellTagText,
	getScheduleSanctuaryLabel,
	getApplicationSanctuaryLabel,
	getUniqueSanctuaryNames,
	filterByVisibleSanctuaries,
	isOfficeSanctuaryUser,
} from "./rentSanctuaryUtils";
import useIsMobile from "../../../hooks/useIsMobile";

dayjs.locale("ko");

const { Content } = Layout;

const ViewRentalSanctuarySchedule = () => {
	const [schedules, setSchedules] = useState([]);
	const [applications, setApplications] = useState([]);
	const [, setLoading] = useState(true);
	const [sanctuaryRoomList, setSanctuaryRoomList] = useState([]);
	const [visibleSanctuaryNames, setVisibleSanctuaryNames] = useState(
		new Set(),
	);
	const [activeTab, setActiveTab] = useState("daily");
	const [selectedDate, setSelectedDate] = useState(dayjs());
	const [selectedWeekStart, setSelectedWeekStart] = useState(() =>
		dayjs().startOf("week"),
	);
	const isMobile = useIsMobile();
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [selectedApplication, setSelectedApplication] = useState(null);
	const [rejectReason, setRejectReason] = useState("");
	const navigate = useNavigate();
	const canManage = isOfficeSanctuaryUser();
	const [calendarPanelDate, setCalendarPanelDate] = useState(() =>
		dayjs(),
	);
	const [holidayMap, setHolidayMap] = useState({});
	const hasInitializedVisibleSanctuaries = useRef(false);

	const sanctuaryNameOptions = useMemo(
		() => getUniqueSanctuaryNames(sanctuaryRoomList),
		[sanctuaryRoomList],
	);

	useEffect(() => {
		if (
			sanctuaryNameOptions.length > 0 &&
			!hasInitializedVisibleSanctuaries.current
		) {
			setVisibleSanctuaryNames(new Set(sanctuaryNameOptions));
			hasInitializedVisibleSanctuaries.current = true;
		}
	}, [sanctuaryNameOptions]);

	useEffect(() => {
		getSanctuaryRoomList();
		getSchedules();
		getApplications();
	}, []);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const y = calendarPanelDate.year();
				const m = calendarPanelDate.month() + 1;
				const map = await fetchKoreanHolidays(y, m);
				if (!cancelled) {
					setHolidayMap(map);
				}
			} catch (e) {
				console.error(e);
				if (!cancelled) {
					setHolidayMap({});
				}
				message.warning(
					"공휴일 정보를 불러오지 못했습니다. 주말만 구분됩니다.",
				);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [calendarPanelDate]);

	const getApplications = async () => {
		try {
			const response = await useAxios.get(
				"/utils/rental/sanctuary/apply/",
			);
			if (response.status === 200) {
				const apps = [...response.data].sort((a, b) =>
					dayjs(a.start_datetime).diff(dayjs(b.start_datetime)),
				);
				setApplications(apps);
			}
		} catch (error) {
			console.error("신청 목록을 불러오는데 실패했습니다.", error);
		}
	};

	const getSanctuaryRoomList = async () => {
		try {
			const response = await useAxios.get("/utils/sanctuary/list/");
			if (response.status === 200) {
				setSanctuaryRoomList(response.data);
			}
		} catch (error) {
			message.error("성전 목록을 불러오는데 실패했습니다.");
		}
	};

	const getSchedules = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				"/utils/rental/sanctuary/schedule/",
			);
			if (response.status === 200) {
				const sorted = [...response.data].sort((a, b) =>
					dayjs(a.start_datetime).diff(dayjs(b.start_datetime)),
				);
				setSchedules(sorted);
			}
		} catch (error) {
			message.error("일정을 불러오는데 실패했습니다.");
			console.error("일정을 불러오는데 실패했습니다.", error);
		} finally {
			setLoading(false);
		}
	};

	const filteredSchedules = useMemo(
		() => filterByVisibleSanctuaries(schedules, visibleSanctuaryNames),
		[schedules, visibleSanctuaryNames],
	);

	const filteredApplications = useMemo(
		() => filterByVisibleSanctuaries(applications, visibleSanctuaryNames),
		[applications, visibleSanctuaryNames],
	);

	const getDaySchedules = useCallback(
		(date) => {
			return filteredSchedules.filter((schedule) =>
				dayjs(schedule.start_datetime).isSame(date, "day"),
			);
		},
		[filteredSchedules],
	);

	const getDayApplications = useCallback(
		(date) => {
			return filteredApplications.filter(
				(application) =>
					dayjs(application.start_datetime).isSame(date, "day") &&
					application.status === "신청",
			);
		},
		[filteredApplications],
	);

	const handleAccept = async (application) => {
		try {
			setLoading(true);
			const response = await useAxios.put(
				`/utils/rental/sanctuary/apply/${application.id}/`,
				{ status: "승인" },
			);
			if (response.status === 200) {
				message.success("성전 대여가 승인되었습니다.");
				getSchedules();
				getApplications();
			}
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"승인 처리 중 오류가 발생했습니다.",
			);
		} finally {
			setLoading(false);
		}
	};

	const handleReject = (application) => {
		setSelectedApplication(application);
		setRejectModalVisible(true);
	};

	const handleRejectConfirm = async () => {
		if (!rejectReason) {
			message.error("거절 사유를 입력해주세요.");
			return;
		}
		try {
			setLoading(true);
			const response = await useAxios.put(
				`/utils/rental/sanctuary/apply/${selectedApplication.id}/`,
				{ status: "거절", reject_reason: rejectReason },
			);
			if (response.status === 200) {
				message.success("신청이 거절되었습니다.");
				setRejectModalVisible(false);
				setRejectReason("");
				getSchedules();
				getApplications();
			}
		} catch (error) {
			message.error("거절 처리 중 오류가 발생했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const handleSanctuaryCheckChange = (checkedValues) => {
		setVisibleSanctuaryNames(new Set(checkedValues));
	};

	const handleSelectAllToggle = () => {
		if (allSelected) {
			setVisibleSanctuaryNames(new Set());
		} else {
			setVisibleSanctuaryNames(new Set(sanctuaryNameOptions));
		}
	};

	const handleTabChange = (key) => {
		if (key === "weekly" && activeTab === "daily") {
			setSelectedWeekStart(selectedDate.startOf("week"));
		}
		if (key === "monthly") {
			if (activeTab === "daily") {
				setCalendarPanelDate(selectedDate.clone());
			} else if (activeTab === "weekly") {
				setCalendarPanelDate(selectedWeekStart.clone());
			}
		}
		setActiveTab(key);
	};

	const handleWeekDayClick = (date) => {
		setSelectedDate(date.clone());
		setActiveTab("daily");
	};

	const handleCalendarDateSelect = (date) => {
		setSelectedDate(date.clone());
		setCalendarPanelDate(date.clone());
		setActiveTab("daily");
	};

	const allSelected =
		sanctuaryNameOptions.length > 0 &&
		sanctuaryNameOptions.every((n) => visibleSanctuaryNames.has(n));

	const renderScheduleItem = (schedule) => (
		<List.Item
			className="utils-rent_sanctuary__daily-schedule__item"
			style={{ padding: "12px 0", borderBottom: "1px solid #f0f0f0" }}>
			<div
				className="utils-rent_sanctuary__daily-schedule__item-content"
				style={{
					display: "flex",
					flexWrap: "wrap",
					gap: "8px",
					width: "100%",
					alignItems: "center",
				}}>
				<div className="utils-rent_sanctuary__daily-schedule__item-tags">
					<Tag
						color={sanctuaryScheduleTagColor(schedule)}
						style={{
							fontSize: isMobile ? "13px" : "14px",
							padding: "4px 12px",
						}}>
						{formatSanctuaryRoomTag(schedule)}
					</Tag>
					<Tag
						color="blue"
						style={{
							fontSize: isMobile ? "13px" : "14px",
							padding: "4px 12px",
						}}>
						{dayjs(schedule.start_datetime).format("HH:mm")} -{" "}
						{dayjs(schedule.end_datetime).format("HH:mm")}
					</Tag>
				</div>
				<div className="utils-rent_sanctuary__daily-schedule__item-info">
					<div className="utils-rent_sanctuary__daily-schedule__item-info-item">
						교구/기관: {schedule.rent_team}
					</div>
				</div>
			</div>
		</List.Item>
	);

	const renderApplicationItem = (application) => (
		<List.Item
			className="utils-rent_sanctuary__daily-schedule__item"
			style={{ padding: "12px 0", borderBottom: "1px solid #f0f0f0" }}>
			<div
				className="utils-rent_sanctuary__daily-schedule__item-content"
				style={{
					display: "flex",
					flexWrap: "wrap",
					gap: "8px",
					width: "100%",
					alignItems: "center",
				}}>
				<div className="utils-rent_sanctuary__daily-schedule__item-tags">
					<Tag
						color="orange"
						style={{
							fontSize: isMobile ? "13px" : "14px",
							padding: "4px 12px",
						}}>
						{formatSanctuaryRoomTag(application)}
					</Tag>
					<Tag
						color="blue"
						style={{
							fontSize: isMobile ? "13px" : "14px",
							padding: "4px 12px",
						}}>
						{dayjs(application.start_datetime).format("HH:mm")} -{" "}
						{dayjs(application.end_datetime).format("HH:mm")}
					</Tag>
				</div>
				<div className="utils-rent_sanctuary__daily-schedule__item-info">
					<div className="utils-rent_sanctuary__daily-schedule__item-info-item">
						교구/기관: {application.rent_team}
					</div>
					<div className="utils-rent_sanctuary__daily-schedule__item-info-item">
						신청자: {application.representative} (
						{application.representative_tel})
					</div>
				</div>
				{canManage && (
					<div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
						<Button
							type="primary"
							size="small"
							onClick={() => handleAccept(application)}>
							승인
						</Button>
						<Button
							danger
							size="small"
							onClick={() => handleReject(application)}>
							거절
						</Button>
					</div>
				)}
			</div>
		</List.Item>
	);

	const renderDailyView = () => {
		const daySchedules = getDaySchedules(selectedDate);
		const dayApplications = getDayApplications(selectedDate);
		const hasSchedules = daySchedules.length > 0;
		const hasApplications = dayApplications.length > 0;

		return (
			<div className="utils-rent_sanctuary__daily-tab">
				<div className="utils-rent_sanctuary__date-nav">
					<Button
						size="middle"
						onClick={() =>
							setSelectedDate(
								selectedDate.subtract(1, "day").clone(),
							)
						}>
						<LeftOutlined />
					</Button>
					<span className="utils-rent_sanctuary__date-nav__label">
						{selectedDate.format("YYYY년 MM월 DD일")}
					</span>
					<Button
						size="middle"
						onClick={() =>
							setSelectedDate(
								selectedDate.add(1, "day").clone(),
							)
						}>
						<RightOutlined />
					</Button>
					<Button size="middle" onClick={() => setSelectedDate(dayjs())}>
						오늘
					</Button>
				</div>

				<div className="utils-rent_sanctuary__daily-schedule">
					{hasSchedules && (
						<>
							<h4 style={{ marginBottom: 0, fontWeight: 600 }}>
								승인된 일정
							</h4>
							<List
								dataSource={daySchedules}
								renderItem={renderScheduleItem}
								style={{ marginBottom: "16px" }}
							/>
						</>
					)}
					{hasApplications && (
						<>
							<h4
								style={{
									marginBottom: "12px",
									marginTop: hasSchedules ? "24px" : "0",
									fontWeight: 600,
								}}>
								신청 대기
							</h4>
							<List
								dataSource={dayApplications}
								renderItem={renderApplicationItem}
								style={{ marginBottom: "16px" }}
							/>
						</>
					)}
					{!hasSchedules && !hasApplications && (
						<p className="utils-rent_sanctuary__daily-schedule__empty">
							해당 날짜의 일정이 없습니다.
						</p>
					)}
					{canManage && (
						<div
							className="utils-rent_sanctuary__daily-schedule__actions"
							style={{ marginTop: "20px", textAlign: "center" }}>
							<Button
								type="primary"
								size={isMobile ? "middle" : "large"}
								onClick={() =>
									navigate(
										`/utils/rent-sanctuary/apply-rent?date=${selectedDate.format("YYYY-MM-DD")}`,
									)
								}
								className="utils-rent_sanctuary__daily-schedule__apply-button"
								style={{
									width: isMobile ? "100%" : "auto",
									minWidth: "150px",
								}}>
								성전 대여 신청
							</Button>
						</div>
					)}
				</div>
			</div>
		);
	};

	const renderWeekDayCell = (date) => {
		const daySchedules = getDaySchedules(date);
		const dayApplications = getDayApplications(date);
		const isToday = date.isSame(dayjs(), "day");

		return (
			<div
				role="button"
				tabIndex={0}
				className={`utils-rent_sanctuary__week-cell${isToday ? " utils-rent_sanctuary__week-cell--today" : ""}`}
				onClick={() => handleWeekDayClick(date)}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						handleWeekDayClick(date);
					}
				}}>
				<div className="utils-rent_sanctuary__week-cell__date">
					{date.format("D")}
					{isToday && (
						<span className="utils-rent_sanctuary__week-cell__today-label">
							오늘
						</span>
					)}
				</div>
				<div className="utils-rent_sanctuary__week-cell__events">
					{daySchedules.map((schedule) => (
						<div
							key={schedule.id}
							className="utils-rent_sanctuary__week-cell__event">
							<Tag
								color={sanctuaryScheduleTagColor(schedule)}
								style={{ margin: 0, fontSize: "11px" }}>
								{truncateCellTagText(
									getScheduleSanctuaryLabel(schedule),
								)}{" "}
								{dayjs(schedule.start_datetime).format("HH:mm")}
							</Tag>
						</div>
					))}
					{dayApplications.map((application) => (
						<div
							key={`app-${application.id}`}
							className="utils-rent_sanctuary__week-cell__event">
							<Tag color="orange" style={{ margin: 0, fontSize: "11px" }}>
								{truncateCellTagText(
									getApplicationSanctuaryLabel(application),
								)}{" "}
								{dayjs(application.start_datetime).format("HH:mm")}
							</Tag>
						</div>
					))}
				</div>
			</div>
		);
	};

	const renderWeeklyView = () => {
		const weekDays = Array.from({ length: 7 }, (_, i) =>
			selectedWeekStart.add(i, "day"),
		);
		const weekEnd = selectedWeekStart.add(6, "day");

		return (
			<div className="utils-rent_sanctuary__weekly-tab">
				<div className="utils-rent_sanctuary__date-nav">
					<Button
						size="middle"
						onClick={() =>
							setSelectedWeekStart(
								selectedWeekStart
									.subtract(1, "week")
									.clone(),
							)
						}>
						<LeftOutlined />
					</Button>
					<span className="utils-rent_sanctuary__date-nav__label">
						{selectedWeekStart.format("YYYY.MM.DD")} ~{" "}
						{weekEnd.format("MM.DD")}
					</span>
					<Button
						size="middle"
						onClick={() =>
							setSelectedWeekStart(
								selectedWeekStart.add(1, "week").clone(),
							)
						}>
						<RightOutlined />
					</Button>
					<Button
						size="middle"
						onClick={() => setSelectedWeekStart(dayjs().startOf("week"))}>
						이번 주
					</Button>
				</div>
				<div className="utils-rent_sanctuary__week-grid">
					{["일", "월", "화", "수", "목", "금", "토"].map((d) => (
						<div
							key={d}
							className="utils-rent_sanctuary__week-grid__header">
							{d}
						</div>
					))}
					{weekDays.map((date) => (
						<div key={date.format("YYYY-MM-DD")}>
							{renderWeekDayCell(date)}
						</div>
					))}
				</div>
			</div>
		);
	};

	const renderCalendarDateBody = useCallback(
		(date) => {
			const daySchedules = getDaySchedules(date);
			const dayApplications = getDayApplications(date);
			const hasSchedules = daySchedules.length > 0;
			const hasApplications = dayApplications.length > 0;

			if (!hasSchedules && !hasApplications) {
				return null;
			}

			if (isMobile) {
				return (
					<div
						className="utils-rent_sanctuary__calendar-cell"
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: 2,
							width: "100%",
						}}>
						{daySchedules.map((schedule) => (
							<Tag
								key={schedule.id}
								color={sanctuaryScheduleTagColor(schedule)}
								style={{
									margin: 0,
									maxWidth: "100%",
									fontSize: "10px",
									lineHeight: 1.2,
									padding: "0 4px",
								}}>
								{truncateCellTagText(
									getScheduleSanctuaryLabel(schedule),
								)}
							</Tag>
						))}
						{dayApplications.map((application) => (
							<Tag
								key={`app-${application.id}`}
								color="orange"
								style={{
									margin: 0,
									maxWidth: "100%",
									fontSize: "10px",
									lineHeight: 1.2,
									padding: "0 4px",
								}}>
								{truncateCellTagText(
									getApplicationSanctuaryLabel(application),
								)}
							</Tag>
						))}
					</div>
				);
			}

			return (
				<div className="utils-rent_sanctuary__calendar-cell">
					{daySchedules.map((schedule) => (
						<div
							key={schedule.id}
							className="utils-rent_sanctuary__calendar-event">
							<div className="utils-rent_sanctuary__calendar-event-header">
								<Tag color={sanctuaryScheduleTagColor(schedule)}>
									{formatSanctuaryRoomTag(schedule)}
								</Tag>
								<Tag color="blue">
									{dayjs(schedule.start_datetime).format("HH:mm")}{" "}
									- {dayjs(schedule.end_datetime).format("HH:mm")}
								</Tag>
							</div>
						</div>
					))}
					{dayApplications.map((application) => (
						<div
							key={application.id}
							className="utils-rent_sanctuary__calendar-event"
							style={{ borderLeft: "3px solid #fa8c16" }}>
							<div className="utils-rent_sanctuary__calendar-event-header">
								<Tag color="orange">
									{formatSanctuaryRoomTag(application)}
								</Tag>
								<Tag color="blue">
									{dayjs(application.start_datetime).format("HH:mm")}{" "}
									-{" "}
									{dayjs(application.end_datetime).format("HH:mm")}
								</Tag>
								<Tag color="default" style={{ fontSize: "11px" }}>
									신청
								</Tag>
							</div>
						</div>
					))}
				</div>
			);
		},
		[getDaySchedules, getDayApplications, isMobile],
	);

	const { fullCellRender } = useMemo(
		() =>
			createCalendarHolidayRenders({
				holidayMap,
				renderDateContent: renderCalendarDateBody,
			}),
		[holidayMap, renderCalendarDateBody],
	);

	const renderMonthlyView = () => (
		<Card className="utils-rent_sanctuary__calendar-card">
			<Calendar
				className="utils-calendar-hide-outside-month-week"
				value={calendarPanelDate}
				headerRender={({ value }) => (
					<Space style={{ padding: 8 }}>
						<Button
							size="middle"
							onClick={() =>
								setCalendarPanelDate(
									value.subtract(1, "month").clone(),
								)
							}>
							<LeftOutlined />
						</Button>
						<span>{value.format("YYYY년 M월")}</span>
						<Button
							size="middle"
							onClick={() => setCalendarPanelDate(dayjs())}>
							오늘
						</Button>
						<Button
							size="middle"
							onClick={() =>
								setCalendarPanelDate(
									value.add(1, "month").clone(),
								)
							}>
							<RightOutlined />
						</Button>
					</Space>
				)}
				fullCellRender={fullCellRender}
				onPanelChange={(date) => setCalendarPanelDate(date)}
				onSelect={handleCalendarDateSelect}
				mode="month"
				locale={{
					lang: {
						locale: "ko",
						monthBeforeYear: true,
						yearFormat: "YYYY년",
						monthFormat: "M월",
						weekFormat: "wo",
						weekStart: 0,
						shortWeekDays: [
							"일",
							"월",
							"화",
							"수",
							"목",
							"금",
							"토",
						],
						shortMonths: [
							"1월",
							"2월",
							"3월",
							"4월",
							"5월",
							"6월",
							"7월",
							"8월",
							"9월",
							"10월",
							"11월",
							"12월",
						],
					},
				}}
			/>
		</Card>
	);

	const tabItems = [
		{ key: "daily", label: "일별", children: renderDailyView() },
		{ key: "weekly", label: "주별", children: renderWeeklyView() },
		{ key: "monthly", label: "월별", children: renderMonthlyView() },
	];

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="성전 대여 일정" />
			<Layout className="member-layout">
				<RentSanctuaryNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="utils-rent_sanctuary__container">
							<div className="utils-rent_sanctuary__header">
								<h1 className="utils-rent_sanctuary__header__title">
									성전 대여 일정
								</h1>
							</div>
							<div className="utils-rent_sanctuary__office-layout">
								<aside className="utils-rent_sanctuary__filter-sidebar">
									<div className="utils-rent_sanctuary__filter-sidebar__header">
										<Checkbox
											checked={allSelected}
											indeterminate={
												visibleSanctuaryNames.size > 0 &&
												!allSelected
											}
											onChange={handleSelectAllToggle}>
											{allSelected ? "전체 해제" : "전체 선택"}
										</Checkbox>
									</div>
									<Checkbox.Group
										value={[...visibleSanctuaryNames]}
										onChange={handleSanctuaryCheckChange}
										className="utils-rent_sanctuary__filter-sidebar__list">
										{sanctuaryNameOptions.map((name) => (
											<div key={name}>
												<Checkbox value={name}>
													{name}
												</Checkbox>
											</div>
										))}
									</Checkbox.Group>
								</aside>
								<div className="utils-rent_sanctuary__schedule-main">
									<Tabs
										activeKey={activeTab}
										onChange={handleTabChange}
										items={tabItems}
										className="utils-rent_sanctuary__schedule-tabs"
									/>
								</div>
							</div>
						</div>

						<Modal
							title="신청 거절"
							open={rejectModalVisible}
							onOk={handleRejectConfirm}
							onCancel={() => {
								setRejectModalVisible(false);
								setRejectReason("");
							}}
							okText="거절"
							cancelText="취소"
							okButtonProps={{ danger: true }}>
							<p>거절 사유를 입력해주세요:</p>
							<Input.TextArea
								value={rejectReason}
								onChange={(e) => setRejectReason(e.target.value)}
								rows={4}
								placeholder="거절 사유를 입력하세요"
								style={{ marginTop: "12px" }}
							/>
						</Modal>
					</Content>
				</Layout>
			</Layout>
		</ConfigProvider>
	);
};

export default ViewRentalSanctuarySchedule;
